import { Button, DataTable, Modal, ResultsCard, ResultsToolbar, RowActions } from 'shared/components/ui';
import React, { useState, useEffect, useMemo } from 'react';
import { Campaign, Add, Search, Refresh } from '@mui/icons-material';
import { Alert, Box, Chip, IconButton, InputAdornment, LinearProgress, Paper, TablePagination, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { useSortableTable } from '../../../shared/hooks/useSortableTable';

import Header from '../../../shared/components/layout/Header';
import Footer from '../../../shared/components/layout/Footer';
import ConfirmModal from '../../../shared/components/ConfirmModal';
import { Toast } from '../../../shared/components/Toast';
import { useCms } from '../hooks/useCms';
import CmsForm from './CmsForm';
import { CMS_PLACEMENTS, getPlacement } from './cmsPlacements';
import './cms.css';
import '../../../features/turnos/components/GrillaTurnos.css';
import '../../../styles/buttons.css';

const formatearVigencia = (desde, hasta) => {
  const fmt = (v) => (v ? v.slice(0, 16).replace('T', ' ') : null);
  const d = fmt(desde);
  const h = fmt(hasta);
  if (!d && !h) return 'Sin límite';
  return `${d || 'inicio'} → ${h || 'sin fin'}`;
};

const GrillaCms = () => {
  const { contenidos, loading, buscar, guardar, actualizar, eliminar, subirMedia } = useCms();
  const [modal, setModal] = useState(null);
  const [confirmEliminar, setConfirmEliminar] = useState(null);
  const [toast, setToast] = useState(null);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('todos');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { sortedData, requestSort, getSortIcon } = useSortableTable(contenidos, 'orden');
  const filtered = useMemo(() => sortedData.filter(item =>
    (status === 'todos' || item.estado === status) &&
    [item.titulo, item.slot, getPlacement(item.slot)?.label, item.audiencia, item.tipo].some(value => String(value || '').toLocaleLowerCase('es').includes(query.trim().toLocaleLowerCase('es')))
  ), [sortedData, query, status]);
  const rows = filtered.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  useEffect(() => { setPage(current => Math.min(current, Math.max(0, Math.ceil(filtered.length / rowsPerPage) - 1))); }, [filtered.length, rowsPerPage]);

  useEffect(() => {
    buscar().catch((err) => setToast({ message: err.message, type: 'error' }));
  }, []); // eslint-disable-line

  const recargar = () =>
    buscar().catch((err) => setToast({ message: err.message, type: 'error' }));

  const abrirNuevo = space => setModal({ mode: 'nuevo', item: space ? { slot: space.slot, audiencia: space.audience, tipo: 'banner', estado: 'borrador' } : null });
  const abrirEditar = (item) => setModal({ mode: 'editar', item });

  const handleSubmit = async (body) => {
    try {
      if (modal.mode === 'editar') {
        await actualizar(modal.item.idContenido, body);
        setToast({ message: 'Contenido actualizado con éxito.', type: 'success' });
      } else {
        await guardar(body);
        setToast({ message: 'Contenido guardado con éxito.', type: 'success' });
      }
      setModal(null);
      recargar();
    } catch (err) {
      setToast({ message: err.message, type: 'error' });
    }
  };

  const toggleEstado = async (item) => {
    const nuevoEstado = item.estado === 'activo' ? 'inactivo' : 'activo';
    const body = {
      Tipo: item.tipo,
      Slot: item.slot,
      Audiencia: item.audiencia,
      Titulo: item.titulo ?? null,
      CuerpoHtml: item.cuerpoHtml ?? null,
      EstilosJson: item.estilosJson ?? null,
      MediaUrl: item.mediaUrl ?? null,
      Orden: item.orden ?? 0,
      Estado: nuevoEstado,
      VigenciaDesde: item.vigenciaDesde ?? null,
      VigenciaHasta: item.vigenciaHasta ?? null,
    };
    try {
      await actualizar(item.idContenido, body);
      setToast({ message: nuevoEstado === 'activo' ? 'Contenido activado.' : 'Contenido desactivado.', type: 'success' });
      recargar();
    } catch (err) {
      setToast({ message: err.message, type: 'error' });
    }
  };

  const confirmarEliminar = async () => {
    try {
      await eliminar(confirmEliminar.id);
      setToast({ message: 'Contenido eliminado con éxito.', type: 'success' });
      recargar();
    } catch (err) {
      setToast({ message: err.message, type: 'error' });
    } finally {
      setConfirmEliminar(null);
    }
  };

  return (
    <div className="grilla-turnos">
      <Header />
      <div className="main-content">
        <div className="page-header">
          <h2 className="page-title-grilla">
            <Campaign sx={{ fontSize: 32, mr: 1, verticalAlign: 'middle', color: '#6d1212' }} />
            CMS Publicidad
          </h2>
          <span className="page-subtitle">Banners y novedades para tus sitios. Elegí una ubicación y diseñá tu publicación.</span>
        </div>

        <Box component="section" aria-label="Ubicaciones de publicidad disponibles" sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
          {CMS_PLACEMENTS.map(space => <Paper key={space.slot} variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="overline" color="primary">{space.site}</Typography>
            <Typography component="h3" sx={{ fontSize: '1.1rem', fontWeight: 650, mb: 1 }}>{space.label.split(' · ')[1]}</Typography>
            <Typography variant="body2" color="text.secondary">{space.position}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>{space.dimensions} · {contenidos.filter(item => item.slot === space.slot).length} {contenidos.filter(item => item.slot === space.slot).length === 1 ? 'publicación' : 'publicaciones'}</Typography>
            <Button variant="outline-primary" size="sm" onClick={() => abrirNuevo(space)} className="mt-3">Crear banner en {space.site}</Button>
          </Paper>)}
        </Box>
        {contenidos.some(item => !getPlacement(item.slot)) && <Alert severity="warning" sx={{ mb: 3 }}>Hay publicaciones con una ubicación no reconocida. Editalas y elegí dónde mostrarlas; actualmente no aparecen en los sitios.</Alert>}

        <Modal show={modal !== null} onHide={() => setModal(null)} size="lg" centered className="cms-modal">
          <Modal.Header closeButton>
            <div>
              <Modal.Title>{modal?.mode === 'editar' ? 'Editar contenido' : 'Nuevo contenido'}</Modal.Title>
              <p className="cms-modal-subtitle">Configurá la publicación, su vigencia y cómo se verá.</p>
            </div>
          </Modal.Header>
          <Modal.Body>
            {modal && (
              <CmsForm
                item={modal.item}
                loading={loading}
                onUploadMedia={subirMedia}
                onSubmit={handleSubmit}
              />
            )}
          </Modal.Body>
          <Modal.Footer>
            <span className="cms-required-note">* Campos obligatorios</span>
            <Button variant="outline-secondary" onClick={() => setModal(null)} disabled={loading}>Cancelar</Button>
            <Button type="submit" form="cms-form" variant="success" disabled={loading}>
              {loading ? 'Guardando…' : 'Guardar cambios'}
            </Button>
          </Modal.Footer>
        </Modal>

        <ResultsCard className="results-card">
          <ResultsToolbar className="results-header">
            <div className="results-header-left"><h3 className="results-title">Resultados</h3></div>
            <div className="results-info">
              <span className="results-count">{filtered.length} {filtered.length === 1 ? 'contenido' : 'contenidos'}</span>
              <Tooltip title="Actualizar contenidos"><span><IconButton aria-label="Actualizar contenidos" size="small" onClick={recargar} disabled={loading}><Refresh /></IconButton></span></Tooltip>
              <Button variant="success" size="sm" onClick={() => abrirNuevo()} className="btn-new-turno">
                <Add sx={{ fontSize: 18, mr: 0.5 }} /> Nuevo contenido
              </Button>
            </div>
          </ResultsToolbar>
          <div className="sugi-grid-search">
            <TextField size="small" label="Buscar contenido" placeholder="Título, sitio o ubicación…" value={query}
              onChange={event => { setQuery(event.target.value); setPage(0); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
            <ToggleButtonGroup size="small" exclusive value={status} aria-label="Filtrar por estado" onChange={(event, next) => { if (next) { setStatus(next); setPage(0); } }}>
              <ToggleButton value="todos">Todos</ToggleButton><ToggleButton value="activo">Activos</ToggleButton><ToggleButton value="borrador">Borradores</ToggleButton><ToggleButton value="inactivo">Inactivos</ToggleButton>
            </ToggleButtonGroup>
          </div>
          {loading && <LinearProgress aria-label="Cargando contenidos" />}
          <div className="table-container table-container-scrollable">
            <DataTable className="data-table" aria-label="Contenidos de publicidad">
              <thead>
                <tr>
                  <th onClick={() => requestSort('slot')}>Ubicación {getSortIcon('slot')}</th>
                  <th>Formato</th>
                  <th onClick={() => requestSort('titulo')}>Título {getSortIcon('titulo')}</th>
                  <th>Público</th>
                  <th>Estado</th>
                  <th onClick={() => requestSort('orden')} style={{ textAlign: 'center' }}>Orden {getSortIcon('orden')}</th>
                  <th>Vigencia</th>
                  <th className="action-cell">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={8} className="empty-state">{loading ? 'Cargando contenidos…' : query || status !== 'todos' ? 'No encontramos contenidos con esos filtros.' : 'Todavía no hay publicaciones. Creá tu primer contenido.'}</td></tr>
                ) : rows.map((c) => (
                  <tr key={c.idContenido}>
                    <td><Typography sx={{ fontSize: '.875rem', fontWeight: 600 }} color={getPlacement(c.slot) ? 'text.primary' : 'warning.main'}>{getPlacement(c.slot)?.label || 'Elegí una ubicación válida'}</Typography></td>
                    <td><Chip size="small" label={c.tipo === 'video' ? 'Con video' : c.mediaUrl ? 'Con imagen' : 'Solo texto'} variant="outlined" /></td>
                    <td style={{ fontWeight: 600 }}>{c.titulo || 'Sin título'}</td>
                    <td>{{ pacientes: 'Portal Pacientes', neuroweb: 'NeuroWeb', newsugi: 'NewSugi', ambos: 'Todas las audiencias' }[c.audiencia] || c.audiencia}</td>
                    <td><Chip size="small" label={c.estado === 'activo' ? 'Activo' : c.estado === 'borrador' ? 'Borrador' : 'Inactivo'} color={c.estado === 'activo' ? 'success' : c.estado === 'borrador' ? 'warning' : 'default'} variant="outlined" /></td>
                    <td style={{ textAlign: 'center' }}>{c.orden}</td>
                    <td>{formatearVigencia(c.vigenciaDesde, c.vigenciaHasta)}</td>
                    <td className="action-cell">
                      <RowActions label={c.titulo || c.slot} disabled={loading} onEdit={() => abrirEditar(c)}
                        active={c.estado === 'activo'} onToggle={() => toggleEstado(c)}
                        onDelete={() => setConfirmEliminar({ id: c.idContenido, label: c.titulo || c.slot })} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          </div>
          <TablePagination component="div" count={filtered.length} page={page} rowsPerPage={rowsPerPage} rowsPerPageOptions={[10, 25, 50]}
            onPageChange={(event, next) => setPage(next)} onRowsPerPageChange={event => { setRowsPerPage(Number(event.target.value)); setPage(0); }}
            labelRowsPerPage="Filas por página" labelDisplayedRows={({ from, to, count }) => from + '–' + to + ' de ' + count}
            getItemAriaLabel={type => type === 'next' ? 'Página siguiente' : 'Página anterior'} />
        </ResultsCard>
      </div>
      <Footer />

      <ConfirmModal
        show={confirmEliminar !== null}
        title="Eliminar Contenido"
        message={`¿Eliminar el contenido "${confirmEliminar?.label}"?`}
        onConfirm={confirmarEliminar}
        onCancel={() => setConfirmEliminar(null)}
      />
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default GrillaCms;
